import { Link, createRoute } from "@tanstack/react-router";
import { ChevronRight, Images, Search, Store } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopHome } from "./desktop/home.tsx";
import { TabPage } from "../components/layout.tsx";
import { Empty, ErrorBlock, Spinner } from "../components/ui.tsx";
import { ProductGrid } from "../components/product-grid.tsx";
import { SpaceIcon } from "../components/space-icon.tsx";
import { useSpaces } from "../lib/site-config.ts";
import { Card } from "@/components/ui/card";
import { useCategories, useIdentities, useMe, useProducts } from "../api/queries.ts";

/**
 * 首页。
 *
 * 🔴 **手机首屏只有一屏的注意力。** 顺序是刻意的：
 *    搜索 → 按空间逛 → 案例 → 品类 → 热销。
 *    「按空间逛」排在品类前面，是因为买建材的人想的是"我家客厅要装什么"，
 *    而不是"我要买瓷砖" —— 后者是已经想清楚的人才有的念头。
 */

function Home() {
  const { data: me } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const { data: categories } = useCategories();
  const { data, isPending, error } = useProducts({ sort: "sales", limit: 20 });
  /** 🔴 后台配的那一排，全站只有这一个来源（见 `lib/site-config.ts` 的 `useSpaces`）。 */
  const spaces = useSpaces();

  /** 我同时是商家时给个入口。⚠️ 一个人既是买家又是分销商是常态，不是特例。 */
  const merchantOrgs = identities?.orgs.filter((o) => o.status === "active") ?? [];
  /** 我作为买家的专属服务商。⚠️ 跟"我是商家"是两回事，一个人可以两样都有。 */
  const myShop = identities?.binding ?? null;

  return (
    <TabPage>
      {/*
        🔴 顶部用主色铺一块，搜索框浮在上面。
           纯白顶栏在手机上跟内容区糊成一片，用户找不到"页面从哪开始"。
      */}
      <header className="bg-gradient-to-b from-brand-600 to-brand-500 px-4 pt-safe-4 pb-5">
        <div className="pt-3">
          <p className="mb-3 text-[13px] text-white/80">装修建材 · 一站购齐</p>
          <Link
            to="/products"
            className="flex h-10 items-center gap-2 rounded-full bg-white px-4 text-[14px] text-muted-foreground shadow-sm"
          >
            <Search className="size-4" />
            搜瓷砖、地板、灯具…
          </Link>
        </div>
      </header>

      <div className="space-y-3 px-3 pb-4">
        {/*
          ⚠️ 这块**往上压 4 单位**盖住顶部色块的下沿 ——
             手机上这个"卡片骑在色块上"的做法能让首屏有层次，
             而不是一条一条平铺下来。
        */}
        <div className="-mt-4">
          {merchantOrgs.length > 0 && (
            <Link to="/merchant" search={{}} className="mb-3 block">
              <Card className="flex items-center gap-3 border-brand-200 bg-brand-50 px-4 py-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
                  <Store className="size-[18px]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-medium text-brand-700">
                    {merchantOrgs[0]!.name}
                  </span>
                  <span className="block text-[12px] text-brand-600/80">
                    {merchantOrgs.length > 1
                      ? `等 ${merchantOrgs.length} 个商家身份 · 去经营`
                      : "去我的店里看看"}
                  </span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-brand-600" />
              </Card>
            </Link>
          )}

          <Card className="px-2 py-3">
            <div className="grid grid-cols-5 gap-1">
              {spaces.map((s) => (
                <Link
                  key={s.key}
                  to="/products"
                  search={{ space: s.key }}
                  className="flex flex-col items-center gap-1.5 rounded-lg py-1.5 active:bg-secondary"
                >
                  {/*
                    ⚠️ 图标用主色描边、底板用浅色。以前是 emoji —— 自带颜色不受控，
                       换配色时它不跟着走，而且每个系统画出来的画风都不一样
                       （见 `components/space-icon.tsx` 开头）。
                  */}
                  <span className="flex size-11 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                    <SpaceIcon icon={s.icon} size={22} />
                  </span>
                  <span className="text-[12px] text-muted-foreground">{s.label}</span>
                </Link>
              ))}
            </div>
          </Card>
        </div>

        {/*
          装修案例入口。建材是低频高客单，用户往往不知道"我家该买什么" ——
          把商品放进一个具体的家里，比商品列表更容易让人下决心。
        */}
        {/*
          🔴 **我的专属服务商。**
             这是这个平台跟普通商城最不一样的一条：买家有一个固定的服务商，
             价格按他给你算、售后找他。不放在首页的话，用户根本不知道有这回事，
             而这恰恰是他能享受到的好处。
        */}
        {myShop && (
          <Link to="/shop/$id" params={{ id: String(myShop.id) }} className="block">
            <Card className="flex items-center gap-3 border-brand-200 bg-brand-50 px-4 py-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-600">
                <Store className="size-[18px] text-white" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-medium text-brand-700">
                  {myShop.name}
                </span>
                <span className="block text-[12px] text-brand-600/80">
                  你的专属服务商 · 他的店里比平台价便宜
                </span>
              </span>
              <ChevronRight className="size-4 shrink-0 text-brand-600" />
            </Card>
          </Link>
        )}

        <Link to="/cases" search={{}} className="block">
          <Card className="flex items-center gap-3 px-4 py-3.5">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary">
              <Images className="size-[18px] text-muted-foreground" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-medium">装修案例</span>
              <span className="block text-[12px] text-muted-foreground">看看别人家怎么装的</span>
            </span>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          </Card>
        </Link>

        {categories && categories.length > 0 && (
          <Card className="px-4 py-4">
            <h2 className="mb-3 text-[13px] font-medium text-muted-foreground">主要品类</h2>
            <div className="flex flex-wrap gap-2">
              {categories.slice(0, 12).map((c) => (
                <Link
                  key={c.id}
                  to="/products"
                  search={{ categoryId: c.id }}
                  className="rounded-full bg-secondary px-3 py-1.5 text-[13px] active:opacity-70"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </Card>
        )}

        <section>
          <div className="flex items-center justify-between px-1 pt-1 pb-2">
            <h2 className="text-[15px] font-medium">热销</h2>
            <Link to="/products" search={{}} className="text-[13px] text-muted-foreground">
              全部 <ChevronRight className="inline size-3.5 -translate-y-px" />
            </Link>
          </div>

          {isPending ? (
            <Spinner rows={3} />
          ) : error ? (
            <ErrorBlock error={error} />
          ) : data?.items.length === 0 ? (
            <Empty title="还没有上架的商品" hint="平台正在招商，很快就有了" />
          ) : (
            <ProductGrid items={data?.items ?? []} myShopName={myShop?.name ?? null} />
          )}
        </section>
      </div>
    </TabPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function HomeRoute() {
  return useIsDesktop() ? <DesktopHome /> : <Home />;
}

export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRoute,
});
